import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { type ReactNode, useEffect } from 'react'
import { listenForSignIn, listenForSignOut } from '@/auth/session-channel'
import { onSignInElsewhere } from '@/auth/useAuth'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { appQueryClient, forgetServerState } from '@/lib/query-client'
import { appPersistOptions } from '@/lib/query-persist'
import { ThemeProvider } from './ThemeProvider'

/**
 * Wires TanStack Query, with its cache persisted to localStorage (lib/query-persist.ts).
 * Auth talks to the Go service directly over fetch (see auth/api.ts) with
 * `credentials: 'include'` so the httpOnly session cookie rides along — no tRPC
 * client.
 *
 * Another tab signing out clears this one too and sends it to /login: the
 * persisted cache is shared across tabs, and a tab left open would otherwise
 * keep showing — and writing back — the last person's data. Another tab signing
 * in makes this one ask who is signed in, and drop the cache if it is someone else.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  useEffect(() => {
    const stopOut = listenForSignOut(() => {
      void forgetServerState().then(() => {
        if (window.location.pathname !== '/login') window.location.href = '/login'
      })
    })
    const stopIn = listenForSignIn((userId) => void onSignInElsewhere(appQueryClient, userId))
    return () => {
      stopOut()
      stopIn()
    }
  }, [])

  return (
    <ThemeProvider>
      <PersistQueryClientProvider client={appQueryClient} persistOptions={appPersistOptions}>
        <TooltipProvider>{children}</TooltipProvider>
        <Toaster />
      </PersistQueryClientProvider>
    </ThemeProvider>
  )
}
